import { useEffect, useState, type FormEvent } from "react";
import { tEnglish as t, type PhraseKey } from "@buza/i18n";
import { api } from "../api.js";
import { ifMatch, RefusalNotice, toRefusal, type Refusal } from "../refusal.js";

// S-094 CR03 Contacts (spec 7.2 CR03, CRM-07). Screens are paused (Duane, 4 Nov 2026): this is the
// minimal screen the server routes need to be used and tested, with phrase keys only and no
// styling work; it moves onto the ZD shell later with CR01 and CR02 (S-090, S-091). The server is
// the control: the search, the role, If-Match, the DELETE confirmation, the privacy request and
// the deletion are all decided by /v1/parlour/crm/contacts; this screen shows its answers.

type Source = "policy_interest" | "passing_enquiry" | "callback";
type Status = "new" | "contacted" | "closed";

export interface ContactRow {
  id: string;
  name: string | null;
  mobile: string;
  items: number;
  openItems: number;
  lastReceivedAt: string | null;
  openDeletionRequest: boolean;
  version: number;
}
export interface ContactList {
  contacts: ContactRow[];
  searchedBy: "all" | "name" | "number";
  more: boolean;
}
export interface ContactDetail {
  contact: { id: string; name: string | null; mobile: string; preferredLocale: string; version: number };
  items: Array<{ id: string; source: Source; status: Status; name: string | null; receivedAt: string; sensitive: boolean }>;
  openDeletionRequest: { id: string; receivedAt: string; dueAt: string } | null;
}

const SOURCE: Record<Source, PhraseKey> = {
  policy_interest: "parlour.crm.contacts.source_policy_interest",
  passing_enquiry: "parlour.crm.contacts.source_passing_enquiry",
  callback: "parlour.crm.contacts.source_callback",
};
const STATUS: Record<Status, PhraseKey> = {
  new: "parlour.crm.contacts.status_new",
  contacted: "parlour.crm.contacts.status_contacted",
  closed: "parlour.crm.contacts.status_closed",
};
const BASE = "/v1/parlour/crm/contacts";

export function Contacts() {
  const [query, setQuery] = useState("");
  const [list, setList] = useState<ContactList | undefined>();
  const [refusal, setRefusal] = useState<Refusal | undefined>();
  const [openId, setOpenId] = useState<string | undefined>();
  const [notice, setNotice] = useState<string | undefined>();

  async function load(q: string) {
    const r = await api<ContactList>(q ? `${BASE}?q=${encodeURIComponent(q)}` : BASE);
    if (!r.ok) {
      setRefusal(toRefusal(r) ?? { kind: "rule", message: t("parlour.crm.contacts.load_failed"), fields: {} });
      return;
    }
    setRefusal(undefined);
    setList(r.data);
  }

  useEffect(() => {
    void load("");
  }, []);

  if (openId) {
    return (
      <ContactDetailView
        id={openId}
        onBack={() => setOpenId(undefined)}
        onDeleted={(message) => {
          setOpenId(undefined);
          setNotice(message);
          void load(query);
        }}
      />
    );
  }

  const search = (e: FormEvent) => {
    e.preventDefault();
    setNotice(undefined);
    void load(query.trim());
  };

  return (
    <section aria-labelledby="cr03-title">
      <h1 id="cr03-title">{t("parlour.crm.contacts.title")}</h1>
      {notice && <p role="status">{notice}</p>}
      <form onSubmit={search} role="search">
        <label htmlFor="cr03-q">{t("parlour.crm.contacts.search_label")}</label>
        <input id="cr03-q" value={query} maxLength={80} onChange={(e) => setQuery(e.target.value)} aria-invalid={refusal?.fields.q ? "true" : undefined} />
        {refusal?.fields.q && <p>{refusal.fields.q}</p>}
        <button type="submit">{t("parlour.crm.contacts.search_submit")}</button>
      </form>
      <RefusalNotice refusal={refusal?.fields.q ? undefined : refusal} />
      {list && list.contacts.length === 0 && <p>{t("parlour.crm.contacts.empty")}</p>}
      {list && list.contacts.length > 0 && (
        <table>
          <thead>
            <tr>
              <th scope="col">{t("parlour.crm.contacts.col_name")}</th>
              <th scope="col">{t("parlour.crm.contacts.col_mobile")}</th>
              <th scope="col">{t("parlour.crm.contacts.col_items")}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {list.contacts.map((c) => (
              <tr key={c.id} data-testid="contact-row">
                <td>{c.name ?? t("parlour.crm.contacts.no_name")}</td>
                <td>{c.mobile}</td>
                <td>{c.items}</td>
                <td>
                  <button type="button" onClick={() => setOpenId(c.id)}>
                    {t("parlour.crm.contacts.open_contact")}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {list?.more && <p>{t("parlour.crm.contacts.more")}</p>}
    </section>
  );
}

function ContactDetailView({ id, onBack, onDeleted }: { id: string; onBack: () => void; onDeleted: (message: string) => void }) {
  const [data, setData] = useState<ContactDetail | undefined>();
  const [confirm, setConfirm] = useState("");
  const [refusal, setRefusal] = useState<Refusal | undefined>();
  const [busy, setBusy] = useState(false);

  async function load() {
    const r = await api<ContactDetail>(`${BASE}/${id}`);
    if (!r.ok) {
      setRefusal(toRefusal(r) ?? { kind: "rule", message: t("parlour.crm.contacts.load_failed"), fields: {} });
      return;
    }
    setRefusal(undefined);
    setData(r.data);
  }

  useEffect(() => {
    void load();
  }, [id]);

  async function remove(e: FormEvent) {
    e.preventDefault();
    if (!data) return;
    setBusy(true);
    const r = await api<{ message: string }>(`${BASE}/${id}/actions/delete`, {
      method: "POST",
      body: { confirm },
      headers: ifMatch(data.contact.version),
    });
    setBusy(false);
    if (!r.ok) {
      // The typed confirmation stays (NFR-15: entries are kept).
      setRefusal(toRefusal(r));
      return;
    }
    onDeleted(r.data.message);
  }

  return (
    <section aria-labelledby="cr03-contact">
      <button type="button" onClick={onBack}>
        {t("parlour.crm.contacts.back")}
      </button>
      {data && (
        <>
          <h1 id="cr03-contact">{data.contact.name ?? t("parlour.crm.contacts.no_name")}</h1>
          <p>{data.contact.mobile}</p>
          <h2>{t("parlour.crm.contacts.items_heading")}</h2>
          <ul>
            {data.items.map((i) => (
              <li key={i.id} data-testid="contact-item">
                <span>{t(SOURCE[i.source])}</span>
                <span>{t(STATUS[i.status])}</span>
                <time dateTime={i.receivedAt}>{i.receivedAt.slice(0, 10)}</time>
              </li>
            ))}
          </ul>
          <form onSubmit={remove} aria-labelledby="cr03-delete">
            <h2 id="cr03-delete">{t("parlour.crm.contacts.delete_heading")}</h2>
            <p>{t("parlour.crm.contacts.delete_intro")}</p>
            <p>{data.openDeletionRequest ? t("parlour.crm.contacts.open_request") : t("parlour.crm.contacts.new_request")}</p>
            <label htmlFor="cr03-confirm">{t("parlour.crm.contacts.confirm_label")}</label>
            <input
              id="cr03-confirm"
              value={confirm}
              autoComplete="off"
              onChange={(e) => setConfirm(e.target.value)}
              aria-invalid={refusal?.fields.confirm ? "true" : undefined}
            />
            {refusal?.fields.confirm && <p>{refusal.fields.confirm}</p>}
            <button type="submit" disabled={busy || confirm !== "DELETE"}>
              {busy ? t("parlour.crm.contacts.deleting") : t("parlour.crm.contacts.delete_submit")}
            </button>
          </form>
        </>
      )}
      <RefusalNotice refusal={refusal} onReload={() => void load()} />
    </section>
  );
}
